import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Plus, FilePdf, ArrowRight, CheckCircle, ClockCountdown, PencilSimple, Trash, PaperPlaneTilt, Eye } from "@phosphor-icons/react";
import NewProposalModal from "@/components/NewProposalModal";
import ManualAcceptModal from "@/components/ManualAcceptModal";
import ConvertProposalModal from "@/components/ConvertProposalModal";
import ReviewModal from "@/components/ReviewModal";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { money } from "@/lib/money";

const shortDate = (iso) => (iso ? new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "—");
const REVIEWABLE = ["pending_review", "changes_requested"];
const STATUS_LABEL = { pending_review: "Needs review", changes_requested: "Changes requested" };

const statusTone = (s, expired) => {
  if (s === "converted") return "bg-emerald-100 text-emerald-800 border-emerald-200";
  if (s === "accepted") return "bg-teal-100 text-teal-800 border-teal-200";
  if (s === "pending_review") return "bg-amber-100 text-amber-900 border-amber-300";
  if (s === "changes_requested") return "bg-orange-100 text-orange-900 border-orange-300";
  if (expired) return "bg-slate-200 text-slate-700 border-slate-300";
  if (s === "sent") return "bg-amber-100 text-amber-800 border-amber-200";
  return "bg-slate-100 text-slate-600 border-slate-200";
};

const FILTERS = [
  { key: "all", label: "All" },
  { key: "review", label: "Needs review", match: (p) => REVIEWABLE.includes(p.status) },
  { key: "sent", label: "Sent", match: (p) => p.status === "sent" },
  { key: "accepted", label: "Accepted", match: (p) => p.status === "accepted" },
  { key: "converted", label: "Converted", match: (p) => p.status === "converted" },
];

export default function Proposals() {
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const filter = FILTERS.some((f) => f.key === params.get("filter")) ? params.get("filter") : "all";
  const [rows, setRows] = useState([]);
  const [reps, setReps] = useState([]);
  const [openNew, setOpenNew] = useState(false);
  const [editing, setEditing] = useState(null); // full proposal doc when editing
  const [busyId, setBusyId] = useState(null);
  const [convertFor, setConvertFor] = useState(null); // { p, isNewClient }
  const [acceptFor, setAcceptFor] = useState(null); // proposal being manually marked accepted
  const [reviewFor, setReviewFor] = useState(null); // rep draft being reviewed inline
  const readOnly = !["admin", "manager"].includes(user.role);
  // Manager can edit proposals that are not yet accepted/converted
  const canEditProposal = (p) => !readOnly && p.status !== "accepted" && p.status !== "converted";
  const repName = (id) => reps.find((r) => r.id === id)?.name || "";

  const load = () => api.get("/proposals").then((r) => setRows(Array.isArray(r.data) ? r.data : []));
  useEffect(() => {
    load();
    if (["admin", "analyst"].includes(user.role)) api.get("/sales-reps").then((r) => setReps(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  }, [user.role]);
  const setFilter = (k) => setParams(k === "all" ? {} : { filter: k }, { replace: true });
  const shown = useMemo(() => { const f = FILTERS.find((x) => x.key === filter); return f?.match ? rows.filter(f.match) : rows; }, [rows, filter]);
  const reviewCount = rows.filter((p) => REVIEWABLE.includes(p.status)).length;

  const openEdit = async (p) => {
    // Fetch the full document to pre-fill (list omits signed_pdf_path but has everything we need)
    try {
      const r = await api.get(`/proposals/${p.id}`);
      setEditing(r.data);
    } catch (e) { toast.error(e.response?.data?.detail?.toString() || e.message); }
  };

  const resend = async (p) => {
    setBusyId(p.id);
    try {
      const r = await api.post(`/proposals/${p.id}/resend`);
      toast.success(r.data?.sent ? "Proposal resent." : "Save succeeded, but the email couldn't be delivered.");
      load();
    } catch (e) { toast.error(e.response?.data?.detail?.toString() || e.message); }
    finally { setBusyId(null); }
  };

  const del = async (p) => {
    if (!window.confirm(`Delete this proposal for "${p.company_name}"?\nThis removes the record and any stored signed PDF. This cannot be undone.`)) return;
    setBusyId(p.id);
    try {
      await api.delete(`/proposals/${p.id}`);
      toast.success("Proposal deleted.");
      load();
    } catch (e) { toast.error(e.response?.data?.detail?.toString() || e.message); }
    finally { setBusyId(null); }
  };

  const openConvert = async (p) => {
    setBusyId(p.id);
    try {
      const r = await api.get("/clients");
      const exists = (Array.isArray(r.data) ? r.data : []).some((c) => c.name === p.company_name);
      setConvertFor({ p, isNewClient: !exists });
    } catch (e) { toast.error(e.response?.data?.detail?.toString() || e.message); }
    finally { setBusyId(null); }
  };

  const stats = {
    sent: rows.filter((r) => r.status === "sent" && !r.is_expired).length,
    accepted: rows.filter((r) => r.status === "accepted").length,
    converted: rows.filter((r) => r.status === "converted").length,
  };

  return (
    <div className="p-4 md:p-8">
      <div className="flex items-start justify-between mb-6 flex-wrap gap-3">
        <div>
          <div className="tinylabel">Recruit &amp; sell</div>
          <h1 className="text-3xl font-black">Proposals</h1>
          <p className="text-slate-500 mt-1">Send stylized safety-services proposals with a secure e-signature link.</p>
        </div>
        {!readOnly && (
          <button data-testid="new-proposal-btn" onClick={() => setOpenNew(true)}
                  className="h-11 px-4 bg-teal-700 hover:bg-teal-800 text-white font-semibold rounded-md flex items-center gap-2">
            <Plus size={18} weight="bold" /> New Proposal
          </button>
        )}
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
        <button type="button" onClick={() => setFilter(filter === "review" ? "all" : "review")} data-testid="proposals-review-stat" className="text-left">
          <StatCard label="Needs review" value={reviewCount} icon={Eye} tone="amber" highlight={reviewCount > 0} />
        </button>
        <StatCard label="Sent · awaiting sig" value={stats.sent} icon={ClockCountdown} tone="amber" />
        <StatCard label="Accepted" value={stats.accepted} icon={CheckCircle} tone="teal" />
        <StatCard label="Converted" value={stats.converted} icon={ArrowRight} tone="emerald" />
      </div>

      <div className="flex flex-wrap gap-1.5 mb-3" data-testid="proposals-filters">
        {FILTERS.map((f) => {
          const n = f.match ? rows.filter(f.match).length : rows.length;
          const active = filter === f.key;
          return (
            <button key={f.key} type="button" onClick={() => setFilter(f.key)} data-testid={`proposal-filter-${f.key}`}
                    className={`h-9 px-3 rounded-full text-sm font-semibold border inline-flex items-center gap-1.5 ${active ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-300 hover:bg-slate-50"}`}>
              {f.label}
              <span className={`text-[11px] px-1.5 rounded-full ${active ? "bg-white/20" : f.key === "review" && n > 0 ? "bg-amber-400 text-amber-950" : "bg-slate-100 text-slate-600"}`} data-testid={`proposal-filter-count-${f.key}`}>{n}</span>
            </button>
          );
        })}
      </div>

      <div className="bg-white border border-slate-200 rounded-md overflow-x-auto">
        <table className="w-full text-sm min-w-[900px]">
          <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-widest">
            <tr>
              <th className="text-left px-4 py-3">Client</th>
              <th className="text-left px-4 py-3">Contact</th>
              <th className="text-left px-4 py-3">Rate</th>
              <th className="text-left px-4 py-3">Sent</th>
              <th className="text-left px-4 py-3">Status</th>
              <th className="text-right px-4 py-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {shown.length === 0 && (
              <tr><td className="px-4 py-8 text-slate-400 text-center" colSpan={6}>
                {rows.length === 0 ? <>No proposals yet — click <b>New Proposal</b> above to send your first one.</> : "Nothing matches this filter."}
              </td></tr>
            )}
            {shown.map((p) => {
              const chip = statusTone(p.status, p.is_expired);
              const label = STATUS_LABEL[p.status] || (p.status === "converted"
                ? "Converted" : p.is_expired && p.status === "sent"
                  ? "Expired" : p.status?.charAt(0).toUpperCase() + p.status?.slice(1));
              const editable = canEditProposal(p);
              const deletable = !readOnly && user.role === "admin";
              const canResend = user.role === "admin" && p.status === "sent";
              const busyRow = busyId === p.id;
              const reviewable = REVIEWABLE.includes(p.status);
              return (
                <tr key={p.id} className={`border-t border-slate-100 hover:bg-slate-50 ${reviewable ? "bg-amber-50/40" : ""}`} data-testid={`proposal-row-${p.id}`}>
                  <td className="px-4 py-3 font-medium">{p.company_name}<div className="text-xs text-slate-500">{p.location}</div>
                    {p.sales_rep_id && <div className="text-[11px] text-emerald-700 font-semibold" data-testid={`proposal-rep-${p.id}`}>Rep: {repName(p.sales_rep_id) || p.submitted_by || "sales rep"}</div>}
                  </td>
                  <td className="px-4 py-3 text-slate-600">{p.contact_name}<div className="text-xs text-slate-500">{p.contact_email}</div></td>
                  <td className="px-4 py-3 font-mono">{money(p.hourly_rate)}/hr{p.per_diem_applies ? <span className="text-xs text-slate-500 font-sans"> + PD</span> : null}</td>
                  <td className="px-4 py-3 text-slate-600">{reviewable ? <span className="text-xs text-slate-400">not sent</span> : shortDate(p.sent_at)}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-semibold border ${chip}`}>{label}</span>
                    {p.status === "changes_requested" && p.review_note && <div className="text-xs text-orange-800 italic mt-1 max-w-[220px] truncate" title={p.review_note}>“{p.review_note}”</div>}
                  </td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    <Link to={`/proposals/${p.id}`} data-testid={`proposal-open-${p.id}`}
                          className="text-teal-700 hover:text-teal-900 font-semibold">Open</Link>
                    {user.role === "admin" && reviewable && (
                      <button data-testid={`proposal-review-${p.id}`} onClick={() => setReviewFor(p)} disabled={busyRow}
                              title="Approve & send to the client, or send back to the rep with a note"
                              className="ml-3 inline-flex items-center gap-1 px-2.5 py-1 rounded bg-amber-500 hover:bg-amber-600 text-white text-xs font-bold align-middle disabled:opacity-40">
                        <Eye size={13} weight="bold" /> Review
                      </button>
                    )}
                    {user.role === "admin" && p.status === "sent" && (
                      <button data-testid={`proposal-accept-manual-${p.id}`} onClick={() => setAcceptFor(p)} disabled={busyRow}
                              title="Client accepted by email / phone / paper — record it so the proposal can be converted"
                              className="ml-3 inline-flex items-center gap-1 px-2.5 py-1 rounded border border-teal-300 text-teal-800 hover:bg-teal-50 text-xs font-bold align-middle disabled:opacity-40">
                        <CheckCircle size={13} weight="bold" /> Mark accepted
                      </button>
                    )}
                    {user.role === "admin" && p.status === "accepted" && (
                      <button data-testid={`proposal-convert-${p.id}`} onClick={() => openConvert(p)} disabled={busyRow}
                              title="Convert this accepted proposal into a Client + Project"
                              className="ml-3 inline-flex items-center gap-1 px-2.5 py-1 rounded bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold align-middle disabled:opacity-40">
                        <ArrowRight size={13} weight="bold" /> Convert
                      </button>
                    )}
                    {editable && (
                      <button data-testid={`proposal-edit-${p.id}`} onClick={() => openEdit(p)} disabled={busyRow}
                              title="Edit terms and resend"
                              className="ml-3 text-slate-500 hover:text-teal-700 align-middle disabled:opacity-40">
                        <PencilSimple size={16} weight="bold" />
                      </button>
                    )}
                    {canResend && (
                      <button data-testid={`proposal-resend-${p.id}`} onClick={() => resend(p)} disabled={busyRow}
                              title="Resend the same proposal (refreshes the 30-day link)"
                              className="ml-3 text-slate-500 hover:text-amber-700 align-middle disabled:opacity-40">
                        <PaperPlaneTilt size={16} weight="bold" />
                      </button>
                    )}
                    {deletable && (
                      <button data-testid={`proposal-delete-${p.id}`} onClick={() => del(p)} disabled={busyRow}
                              title="Delete proposal"
                              className="ml-3 text-slate-400 hover:text-rose-600 align-middle disabled:opacity-40">
                        <Trash size={16} weight="bold" />
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {openNew && <NewProposalModal onClose={() => setOpenNew(false)} onCreated={load} />}
      {editing && <NewProposalModal initial={editing} onClose={() => setEditing(null)} onCreated={load} />}
      {acceptFor && <ManualAcceptModal proposal={acceptFor} onClose={() => setAcceptFor(null)} onAccepted={load} />}
      {reviewFor && <ReviewModal kind="proposal" doc={reviewFor} repName={repName(reviewFor.sales_rep_id) || reviewFor.submitted_by} onClose={() => setReviewFor(null)} onDone={load} />}

      {convertFor && (
        <ConvertProposalModal proposal={convertFor.p} isNewClient={convertFor.isNewClient}
          onClose={() => setConvertFor(null)} onConverted={() => { setConvertFor(null); load(); }} />
      )}
    </div>
  );
}

function StatCard({ label, value, icon: Icon, tone, highlight }) {
  return (
    <div className={`bg-white border rounded-md p-4 h-full ${highlight ? "border-amber-400 ring-2 ring-amber-200" : "border-slate-200"}`}>
      <div className="flex items-start justify-between">
        <div>
          <div className="tinylabel">{label}</div>
          <div className="text-3xl font-black mt-1">{value}</div>
        </div>
        <div className={`p-2 rounded-md bg-${tone}-100 text-${tone}-700`}><Icon size={22} weight="duotone" /></div>
      </div>
    </div>
  );
}
